/**
 * "This visitor clicked Upgrade to Plus while signed out" — carried through sign-in so
 * checkout can start once they're in.
 *
 * The plan travels in the URL (`/login?upgrade=plus`) so it survives the hop from the
 * marketing domain to the app domain, then sits in sessionStorage on the app origin so it
 * survives the Google OAuth round trip (which always lands back on `/`). Checkout itself
 * always requires the server-side session — this only remembers the choice.
 */
import type { PlanId } from "@/lib/billing";

export type PaidPlanId = Exclude<PlanId, "starter">;

const KEY = "unfold_upgrade_intent";
/** An intent older than this is forgotten (they wandered off before signing in). */
const MAX_AGE_MS = 30 * 60 * 1000;

export function parsePaidPlan(value: string | null | undefined): PaidPlanId | null {
  return value === "plus" || value === "premium" ? value : null;
}

/** The `?upgrade=` plan on the current URL, if any. */
export function upgradePlanFromUrl(): PaidPlanId | null {
  if (typeof window === "undefined") return null;
  return parsePaidPlan(new URLSearchParams(window.location.search).get("upgrade"));
}

export function rememberUpgradeIntent(plan: PaidPlanId): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ plan, at: Date.now() }));
  } catch {
    // Private mode / quota — the URL parameter still carries it through a plain login.
  }
}

/** Read and clear the remembered plan (at most once). */
export function takeUpgradeIntent(): PaidPlanId | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as { plan?: string; at?: number };
    if (typeof parsed.at !== "number" || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsePaidPlan(parsed.plan);
  } catch {
    return null;
  }
}
